import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { RootState } from 'redux/store';
import FormInput from 'components/Form/FormInput';
import { cleanDispensePricingStockState, updateDispensePricingField } from 'redux/slices/DispensePricing';
import { useToggle } from 'hooks';
import { FormatDateToDMY, RemoveTimeFromDate } from 'helpers/data/dataHandling';
import { ProductsSpecialPricingExtended } from 'models/storeModels/ProductsSpecialPricing';
import { onlyUnique } from 'helpers/Functions';

type StockPopupProps = {
	show: boolean;
	handleClose: () => void;
	handleSave: (stockChanged: boolean) => void;
}

export function StockPopup(props: StockPopupProps) {
	const dispatch = useAppDispatch();
	const dispensePricing = useAppSelector((state: RootState) => state.dispensePricing);
	const [isValidated, setValidated] = useToggle();
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const form = useRef(null);

	useEffect(() => {
		dispatch(cleanDispensePricingStockState());
		if(dispensePricing.table[dispensePricing.stockIndex]) {
			const {
				name,
				productID,
				productsSpecialPricingID,
				privateFixedPrice,
				privateFixedPricePacks,
				privateFixedPriceStart,
				privateFixedPriceEnd,
				pbsFixedPrice,
				pbsFixedPricePacks,
				pbsFixedPriceStart,
				pbsFixedPriceEnd,
				clubFixedPrice,
				clubFixedPricePacks,
				clubFixedPriceStart,
				clubFixedPriceEnd
			} = dispensePricing.table[dispensePricing.stockIndex];
			handleChange('stockName', name);
			handleChange('stockId', productID);
			handleChange('productsSpecialPricingID', productsSpecialPricingID);
			handlePriceBlur('privateFixedPrice', privateFixedPrice);
			handleQtyBlur('privateFixedPricePacks', privateFixedPricePacks);
			handleChange('privateFixedPriceStart', RemoveTimeFromDate(privateFixedPriceStart));
			handleChange('privateFixedPriceEnd', RemoveTimeFromDate(privateFixedPriceEnd));
			handlePriceBlur('pbsFixedPrice', pbsFixedPrice);
			handleQtyBlur('pbsFixedPricePacks', pbsFixedPricePacks);
			handleChange('pbsFixedPriceStart', RemoveTimeFromDate(pbsFixedPriceStart));
			handleChange('pbsFixedPriceEnd', RemoveTimeFromDate(pbsFixedPriceEnd));
			handlePriceBlur('clubFixedPrice', clubFixedPrice);
			handleQtyBlur('clubFixedPricePacks', clubFixedPricePacks);
			handleChange('clubFixedPriceStart', RemoveTimeFromDate(clubFixedPriceStart));
			handleChange('clubFixedPriceEnd', RemoveTimeFromDate(clubFixedPriceEnd));
		}
	}, [dispensePricing.stockIndex, dispensePricing.stockPopup]);

	function handleChange(option: any, optionValue: any) {
		dispatch(updateDispensePricingField({ key: option, value: optionValue }));
	}

	// function handleModelChange(option: any, optionValue: any) {
	// 	dispatch(updateDispensePricingModelField({ key: option, value: optionValue }));
	// }

	function handleSelectedStockChange(option: any, optionValue: any) {
		dispatch(updateDispensePricingField({key: option, value: optionValue === "" ? null : optionValue}));
	}

	function handlePriceBlur(option: any, optionValue: any) {
		const parsedVal = parseFloat(optionValue);
		if (!isNaN(parsedVal)) {
			dispatch(updateDispensePricingField({ key: option, value: parsedVal.formatAsPrice() }))
		} else {
			dispatch(updateDispensePricingField({ key: option, value: optionValue }));
		}
	}

	function handleQtyBlur(option: any, optionValue: any) {	
		const parsedVal = parseInt(optionValue);
		if (!isNaN(parsedVal)) {
			dispatch(updateDispensePricingField({ key: option, value: parsedVal }))
		} else {
			dispatch(updateDispensePricingField({ key: option, value: optionValue }));
		}
	}

	function saveSelectedStock() {
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			const stock = {
				name: dispensePricing.stockName,
				productID: dispensePricing.stockId,
				productsSpecialPricingID: dispensePricing.productsSpecialPricingID,
				privateFixedPrice: parseFloat(emptyCheck(dispensePricing.privateFixedPrice)),
				privateFixedPricePacks: parseInt(emptyCheck(dispensePricing.privateFixedPricePacks)),
				privateFixedPriceStart: emptyCheck(dispensePricing.privateFixedPriceStart),
				privateFixedPriceEnd: emptyCheck(dispensePricing.privateFixedPriceEnd),
				pbsFixedPrice: parseFloat(emptyCheck(dispensePricing.pbsFixedPrice)),
				pbsFixedPricePacks: parseInt(emptyCheck(dispensePricing.pbsFixedPricePacks)),
				pbsFixedPriceStart: emptyCheck(dispensePricing.pbsFixedPriceStart),
				pbsFixedPriceEnd: emptyCheck(dispensePricing.pbsFixedPriceEnd),
				clubFixedPrice: parseFloat(emptyCheck(dispensePricing.clubFixedPrice)),
				clubFixedPricePacks: parseInt(emptyCheck(dispensePricing.clubFixedPricePacks)),
				clubFixedPriceStart: emptyCheck(dispensePricing.clubFixedPriceStart),
				clubFixedPriceEnd: emptyCheck(dispensePricing.clubFixedPriceEnd)
			}

			if (stock.privateFixedPrice === null && stock.privateFixedPricePacks === null &&
				stock.pbsFixedPrice === null && stock.pbsFixedPricePacks === null && 
				stock.clubFixedPrice === null && stock.clubFixedPricePacks === null) {
					setErrorMsg("Please ensure at least one row of both Price & Qty fields are filled.");
					return;
			}
			
			if(!validatePriceAndQty(stock.privateFixedPrice, stock.privateFixedPricePacks) ||
				!validatePriceAndQty(stock.pbsFixedPrice, stock.pbsFixedPricePacks) || 
				!validatePriceAndQty(stock.clubFixedPrice, stock.clubFixedPricePacks)) {
				setErrorMsg("Please ensure both the Price and Qty fields are either filled or empty.");
				return;
			}

			// Check if any of the values in the stock have changed, and pass this to the handleSave function
			const prevStock = dispensePricing.table[dispensePricing.stockIndex];

			// Get the keys of the new and previous stock objects
			const newStockKeys = Object.keys(stock) as Array<keyof typeof stock>;
			const prevStockKeys = Object.keys(prevStock) as Array<keyof ProductsSpecialPricingExtended>;

			let stockChanged = false;
			// Check overlapping properties between the new and previous stock objects, and see if the values are different, if they are set the flag
			newStockKeys.filter(key => prevStockKeys.includes(key as keyof ProductsSpecialPricingExtended)).forEach(key => {
				if (stock[key] !== prevStock[key as keyof ProductsSpecialPricingExtended]) {
					stockChanged = true;
					return;
				}
			});
			handleChange('disableAutoSelectReset', true);
			handleChange('table', [...dispensePricing.table.slice(0, dispensePricing.stockIndex), stock, ...dispensePricing.table.slice(dispensePricing.stockIndex + 1)]);
			
			// We need to wait for the table to update before we can trigger the flag to re-enable the auto select reset on the React Table
			// otherwise the flag will be reset before the table finishes updating and react table will deselect all the rows
			setTimeout(() => {
				handleChange('disableAutoSelectReset', false);
			}, 300);

			dispatch(cleanDispensePricingStockState());
			props.handleSave(stockChanged);
		} else {
			handleChange("loading", false);
		}
		setErrorMsg(null);
		setValidated();
	}

	// If the value is an empty string, return null, otherwise return the original value
	function emptyCheck(value: any) {
		return value === "" ? null : value;
	}

	// This function makes sure that price and qty are either both null or both non-null, if only one has a value, it returns false
	function validatePriceAndQty(price: number | null, qty: number | null): boolean {
		if ((price === null && qty !== null) || (price !== null && qty === null)) {
			return false;
		}

		return true;
	}

	return (
		<>
			<Modal
				size="lg"
				centered
				show={props.show}
				onHide={props.handleClose}
			>
				<Modal.Header closeButton>
					<Row xs={12}>
						<h5 style={{marginBlockEnd:"0px"}}>{dispensePricing.stockName}</h5>
					</Row>
				</Modal.Header>
				<Modal.Body>
					<Form onSubmit={saveSelectedStock} validated={isValidated} ref={form}>
						<Row className='mt-1'>
							<Col xs={3}>
								<FormInput
									prepend="$"
									type="number"
									min={0.01}
									step={0.01}
									label="Private Price"
									containerClass={'mb-3'}
									placeholder="Enter Price"
									name="dispense-pricing-private-price"
									value={dispensePricing.privateFixedPrice ?? ""}
									onBlur={(e) => handlePriceBlur("privateFixedPrice", e.target.value)}
									onChange={(e) => handleSelectedStockChange('privateFixedPrice', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									label="Qty"
									type="number"
									min={1}
									name="dispense-pricing-private-qty"
									placeholder="Enter Qty"
									containerClass={'mb-3'}
									value={dispensePricing.privateFixedPricePacks ?? ""}
									onBlur={(e) => handleQtyBlur('privateFixedPricePacks', e.target.value)}
									onChange={(e) => handleSelectedStockChange('privateFixedPricePacks', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									max={dispensePricing.privateFixedPriceEnd ?? undefined}
									type="date"
									label="Start Date"
									name="dispense-pricing-private-start-date"
									placeholder="Enter Start Date"
									containerClass={'mb-3'}
									value={dispensePricing.privateFixedPriceStart ?? ""}
									onChange={(e) => handleSelectedStockChange('privateFixedPriceStart', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									min={dispensePricing.privateFixedPriceStart ?? undefined}
									type="date"
									label="End Date"
									name="dispense-pricing-private-end-date"
									placeholder="Enter End Date"
									containerClass={'mb-3'}
									value={dispensePricing.privateFixedPriceEnd ?? ""}
									onChange={(e) => handleSelectedStockChange('privateFixedPriceEnd', e.target.value)}
								/>
							</Col>
						</Row>
						<Row>
							<Col xs={3}>
								<FormInput
									label="PBS Price"
									prepend='$'
									type="number"
									min={0.01}
									step={0.01}
									name="dispense-pricing-private-price"
									placeholder="Enter Price"
									containerClass={'mb-3'}
									value={dispensePricing.pbsFixedPrice ?? ""}
									onBlur={(e) => handlePriceBlur("pbsFixedPrice", e.target.value)}
									onChange={(e) => handleSelectedStockChange('pbsFixedPrice', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									label="Qty"
									type="number"
									min={1}
									name="dispense-pricing-qty"
									placeholder="Enter Qty"
									containerClass={'mb-3'}
									value={dispensePricing.pbsFixedPricePacks ?? ""}
									onBlur={(e) => handleQtyBlur('pbsFixedPricePacks', e.target.value)}
									onChange={(e) => handleSelectedStockChange('pbsFixedPricePacks', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									max={dispensePricing.pbsFixedPriceEnd ?? undefined}
									label="Start Date"
									type="date"
									name="dispense-pricing-start-date"
									placeholder="Enter Start Date"
									containerClass={'mb-3'}
									value={dispensePricing.pbsFixedPriceStart ?? ""}
									onChange={(e) => handleSelectedStockChange('pbsFixedPriceStart', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									min={dispensePricing.pbsFixedPriceStart ?? undefined}
									label="End Date"
									type="date"
									name="dispense-pricing-end-date"
									placeholder="Enter End Date"
									containerClass={'mb-3'}
									value={dispensePricing.pbsFixedPriceEnd ?? ""}
									onChange={(e) => handleSelectedStockChange('pbsFixedPriceEnd', e.target.value)}
								/>
							</Col>
						</Row>
						<Row className='mb-1'>
							<Col xs={3}>
								<FormInput
									prepend='$'
									label="Club Price"
									type="number"
									min={0.01}
									step={0.01}
									name="dispense-pricing-private-price"
									placeholder="Enter Price"
									containerClass={'mb-3'}
									value={dispensePricing.clubFixedPrice ?? ""}
									onBlur={(e) => handlePriceBlur("clubFixedPrice", e.target.value)}
									onChange={(e) => handleSelectedStockChange('clubFixedPrice', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									label="Qty"
									type="number"
									min={1}
									name="dispense-pricing-qty"
									placeholder="Enter Qty"
									containerClass={'mb-3'}
									value={dispensePricing.clubFixedPricePacks ?? ""}
									onBlur={(e) => handleQtyBlur('clubFixedPricePacks', e.target.value)}
									onChange={(e) => handleSelectedStockChange('clubFixedPricePacks', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									max={dispensePricing.clubFixedPriceEnd ?? undefined}
									label="Start Date"
									type="date"
									name="dispense-pricing-start-date"
									placeholder="Enter Start Date"
									containerClass={'mb-3'}
									value={dispensePricing.clubFixedPriceStart ?? ""}
									onChange={(e) => handleSelectedStockChange('clubFixedPriceStart', e.target.value)}
								/>
							</Col>
							<Col xs={3}>
								<FormInput
									min={dispensePricing.clubFixedPriceStart ?? undefined}
									label="End Date"
									type="date"
									name="dispense-pricing-end-date"
									placeholder="Enter End Date"
									containerClass={'mb-3'}
									value={dispensePricing.clubFixedPriceEnd ?? ""}
									onChange={(e) => handleSelectedStockChange('clubFixedPriceEnd', e.target.value)}
								/>
							</Col>
						</Row>
						<Modal.Footer>
							{errorMsg &&
								<Alert variant="danger" className="me-2 mb-0 py-1 px-2 bg-transparent text-danger">
									<i className="mdi mdi-alert-circle-outline me-2"></i>{errorMsg}
								</Alert>
							}
							<Button variant="secondary" className="cta-button" onClick={() => handleChange('stockPopup', false)}>Cancel</Button>
							<Button variant="primary" className="cta-button" onClick={saveSelectedStock}>Save</Button>
						</Modal.Footer>
					</Form>
				</Modal.Body>
			</Modal>
		</>
	);
}